import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { SearchHit } from '../../models/search-hit.model';
import { SearchResult } from '../../models/search-result.model';

interface CardView {
  index: number;
  hit: SearchHit;
}

/** The include_* toggles whose fields a card can show, for the "turn
 *  these on" tip when no article carries them. URL and domain name aren't
 *  listed: the card always shows a domain, read off the URL if needed.
 *  Content isn't either: it's only there where the licence allows. */
const OPTIONAL_FIELDS: { label: string; has: (h: SearchHit) => boolean }[] = [
  { label: 'Title', has: h => !!h.title },
  { label: 'Description', has: h => !!h.description },
  { label: 'Image URL', has: h => !!h.imageUrl },
  { label: 'Publication date', has: h => !!h.publicationDate },
  { label: 'Favicon', has: h => !!h.favicon },
  { label: 'Relevance score', has: h => h.relevanceScore !== null },
  { label: 'Author', has: h => !!h.author },
  { label: 'Language', has: h => h.languages.length > 0 },
  { label: 'Indexation date', has: h => !!h.indexationDate },
];

/** What each response key means, for the hover tips on the cards
 *  (wording follows the API docs' `search_hit` / `search_meta` schemas). */
const FIELD_TIPS = {
  title: 'The article title. Click to open the original article.',
  description: "The article's description, as the source published it.",
  imageUrl: "The article's lead image.",
  publicationDate: 'When the source says it was published.',
  indexationDate: 'When Cikisi saw it, later than the publication date, sometimes much later.',
  relevanceScore: "Elasticsearch's own score for this article, not normalized to 0–1. Higher is a closer match.",
  domainName: 'The site the article was published on. Click to open the original article.',
  url: 'The article itself. Shown as its domain; click to open it.',
  author: 'Who wrote the article, as the source credits it.',
  language: 'The language the article is written in.',
  content: "The article's full text, returned only where the source's licence allows it.",
  count: 'Articles returned, after Max results was applied.',
  totalMatches: 'How many articles matched before the Max results cut.',
} as const;

const PAGE_SIZE = 24;

/**
 * /search's Response tab: the articles as cards in a grid, like Cikisi's
 * item cards: image, date, title, description, then the site. Reads only
 * what the response carries; fields behind an off include_* toggle are
 * left out, with one tip naming the toggles that would fill them in. Top
 * sites (../../top-sites) filters the grid by domain, several at once.
 */
@Component({
  selector: 'app-search-results',
  standalone: false,
  templateUrl: './search-results.html',
  styleUrl: './search-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResults {
  readonly result = input.required<SearchResult>();

  protected readonly visible = signal(PAGE_SIZE);
  /** Show only articles from these domains (Top sites); empty = all. */
  protected readonly siteFilter = signal<ReadonlySet<string>>(new Set());
  /** Cards whose image failed to load: they switch to the no-image layout. */
  protected readonly brokenImages = signal<ReadonlySet<number>>(new Set());
  /** Cards showing their full text. */
  protected readonly contentOpen = signal<ReadonlySet<number>>(new Set());
  protected readonly tips = FIELD_TIPS;

  private readonly cards = computed<CardView[]>(() => this.result().results.map((hit, index) => ({ index, hit })));

  protected readonly filteredCards = computed(() => {
    const sites = this.siteFilter();
    return sites.size ? this.cards().filter(c => sites.has(c.hit.host)) : this.cards();
  });

  protected readonly shownCards = computed(() => this.filteredCards().slice(0, this.visible()));

  /** Optional fields that no article carries, i.e. toggles that are off. */
  protected readonly missingFields = computed(() => {
    const hits = this.result().results;
    if (!hits.length) return [];
    return OPTIONAL_FIELDS.filter(f => !hits.some(f.has)).map(f => f.label);
  });

  protected toggleSite(host: string): void {
    this.siteFilter.update(set => toggled(set, host));
    this.visible.set(PAGE_SIZE);
  }

  protected clearSites(): void {
    this.siteFilter.set(new Set());
    this.visible.set(PAGE_SIZE);
  }

  protected showMore(): void {
    this.visible.update(n => n + PAGE_SIZE);
  }

  /** The order the backend says it used (meta.sort_by / sort_direction). */
  protected readonly sortHint = computed(() => {
    const { meta, results } = this.result();
    if (!meta.sortBy || !results.length) return null;
    if (meta.sortBy === 'relevance') return 'Best match first';
    const asc = meta.sortDirection === 'asc';
    const what = meta.sortBy === 'indexation_date' ? 'indexed' : 'published';
    return `${asc ? 'Oldest' : 'Newest'} ${what} first`;
  });

  protected hasImage(c: CardView): boolean {
    return !!c.hit.imageUrl && !this.brokenImages().has(c.index);
  }

  protected imageFailed(index: number): void {
    this.brokenImages.update(set => new Set(set).add(index));
  }

  protected toggleContent(index: number): void {
    this.contentOpen.update(set => toggled(set, index));
  }

  /** A favicon that 404s shouldn't leave a broken-image icon. */
  protected hideBrokenImage(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}

function toggled<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  return next;
}
